<template>
  <v-menu
    v-model="open"
    offset-y
    left
    nudge-bottom="6"
    min-width="0"
    content-class="app-menu"
    role="none"
  >
    <template v-slot:activator="{ on, attrs }">
      <button
        type="button"
        class="app-ib msg-bell"
        :style="sizeStyle"
        :aria-label="label"
        :title="touch ? null : 'Nachrichten'"
        v-bind="attrs"
        v-on="on"
      >
        <app-icon name="bell" :size="iconSize" :style="iconStyle" />
        <span v-if="unread" class="msg-bell__dot" aria-hidden="true"></span>
      </button>
    </template>
    <div class="msg-menu" :class="{ 'msg-menu--touch': touch }" role="menu" aria-label="Nachrichten">
      <div class="app-menu__header" role="presentation" aria-hidden="true">Nachrichten</div>
      <button
        v-for="message in list"
        :key="message.id"
        type="button"
        class="v-list-item app-menu__item msg-menu__item"
        role="menuitem"
        @click="show(message.id)"
      >
        <span class="msg-menu__dot" :class="{ 'msg-menu__dot--unread': !isRead(message.id) }" aria-hidden="true"></span>
        <span class="msg-menu__text">
          <span class="msg-menu__title">
            {{ message.title }}<span v-if="!isRead(message.id)" class="app-sr-only"> (ungelesen)</span>
          </span>
          <span class="msg-menu__teaser">{{ message.teaser }}</span>
          <span class="msg-menu__date">{{ message.dateText }}</span>
        </span>
      </button>
    </div>
  </v-menu>
</template>

<script>
// Nachrichten oben rechts in der Systemleiste (Kundenwunsch 13.10.2026: „oben rechts noch ein benachrichtigungsicon mit
// messages“): runder Knopf wie „Darstellung“ und der Verbindungsknopf (32, Touch 44, Glocke 16/20 in --ink-3), solange
// etwas ungelesen ist mit orangem Punkt (--acc) oben rechts. Menü „Nachrichten“ (rechtsbündig darunter, 300 breit, Touch
// 320): je Nachricht Punkt (ungelesen orange), Titel, Kurztext und Datum; ein Klick öffnet sie im Dialog
// (AppMessageDialog, src/shell/messages.js) und gilt als gelesen. Prop size wie AppThemeToggle.
import AppIcon from '../ui/AppIcon.vue';
import { notePopup } from '../../shell/shellState';
import { MESSAGES, messages, isRead, openMessage } from '../../shell/messages';

export default {
  name: 'AppMessages',
  components: { AppIcon },
  props: {
    size: { type: [String, Number], default: 'auto' }
  },
  data() {
    return {
      open: false,
      list: MESSAGES,
      state: messages
    };
  },
  computed: {
    touch() {
      return !!(this.$dichte && this.$dichte.touch);
    },
    unread() {
      // state.read reaktiv lesen
      return this.state.read && MESSAGES.some(m => !isRead(m.id));
    },
    label() {
      return this.unread ? 'Nachrichten, ungelesene vorhanden' : 'Nachrichten';
    },
    fixedSize() {
      const n = Number(this.size);
      return this.size !== 'auto' && n > 0 ? n : null;
    },
    sizeStyle() {
      return this.fixedSize ? { width: this.fixedSize + 'px', height: this.fixedSize + 'px' } : null;
    },
    iconSize() {
      const s = this.fixedSize;
      if (!s) {
        return 16;
      }
      return s >= 44 ? 20 : (s >= 36 ? 18 : 16);
    },
    iconStyle() {
      return this.fixedSize ? { width: this.iconSize + 'px', height: this.iconSize + 'px' } : null;
    }
  },
  watch: {
    open(isOpen) {
      notePopup(isOpen);
    }
  },
  beforeDestroy() {
    if (this.open) {
      notePopup(false);
    }
  },
  methods: {
    isRead(id) {
      return this.state.read && isRead(id);
    },
    show(id) {
      this.open = false;
      openMessage(id);
    }
  }
};
</script>

<style scoped>
.msg-bell {
  position: relative;
  border-radius: 50%;
  color: var(--ink-3);
}
:where(html:not(.dichte-touch)) .msg-bell:hover {
  color: var(--ink-2);
}
/* ungelesen: oranger Punkt oben rechts mit Ring in der Fläche */
.msg-bell__dot {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 8px;
  height: 8px;
  box-sizing: border-box;
  border-radius: 50%;
  background: var(--acc);
  box-shadow: 0 0 0 2px var(--surface-raised);
}
.msg-menu {
  display: flex;
  flex-direction: column;
  width: 300px;
  max-width: calc(100vw - 32px);
}
.msg-menu--touch {
  width: 320px;
}
.msg-menu__item.app-menu__item {
  align-items: flex-start;
  gap: 10px;
  height: auto;
  min-height: 0;
  padding-top: 8px;
  padding-bottom: 8px;
  text-align: left;
}
.msg-menu__dot {
  flex: none;
  width: 8px;
  height: 8px;
  margin-top: 6px;
  border-radius: 50%;
}
.msg-menu__dot--unread {
  background: var(--acc);
}
.msg-menu__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.msg-menu__title {
  font-size: 13px;
  line-height: 20px;
  font-weight: 600;
  color: var(--ink);
}
.msg-menu__teaser {
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-2);
  white-space: normal;
}
.msg-menu__date {
  font-size: 11px;
  line-height: 16px;
  color: var(--ink-3);
}
@media print {
  .msg-bell {
    display: none;
  }
}
</style>
